<template>
  <div class="wg-card" id="wg-urls">
    <div class="wg-card-head">
      <div class="wg-sec-title">Published Content URLs</div>
      <span :class="tracked.length ? 'wg-badge-green' : 'wg-badge-grey'">{{ tracked.length }} tracked</span>
    </div>
    <div class="wg-sec-sub">
      When you publish articles, add the live URLs here so we can track performance in your Wins
      reports. Tracking starts as soon as a URL is added.
    </div>

    <!-- tracked list / empty state -->
    <div v-if="tracked.length === 0" class="wg-cempty">
      <i class="material-icons">link_off</i>
      <div class="t">{{ needsUrl.length ? 'No URLs tracked yet' : 'Nothing published yet' }}</div>
      <div class="s">
        {{ needsUrl.length
          ? "Add the live URL for each delivered article below. We'll start collecting impressions, clicks and rankings from the next data pull."
          : adminCanAdd
            ? 'When your POP team delivers an article, it appears here so you can add the live URL. You can also add any page you want tracked using the form below.'
            : 'When your POP team delivers an article, it appears here so you can add the live URL.' }}
      </div>
    </div>

    <template v-else>
      <div v-if="lostCount" class="wg-notice">
        <span class="ico"><i class="material-icons-outlined">warning</i></span>
        <div class="body">
          <div class="nt">
            {{ lostCount }} tracked {{ lostCount === 1 ? 'URL' : 'URLs' }} stopped returning data
          </div>
          <div class="nb">
            Google reported no impressions for it in our last two data pulls. This usually means
            the address changed. Update it below to resume tracking; its history is kept.
          </div>
        </div>
      </div>

      <div v-for="row in tracked" :key="row.id" class="wg-urlrow" :class="{ lost: row.lostSignal }">
        <div class="left">
          <a class="u" :href="row.url" target="_blank" rel="noopener noreferrer">{{ row.url }}</a>
          <div class="meta">
            Keyword: {{ row.keyword || '–' }}
            <span class="wg-tag" :class="tagClass(row.source)">{{ tagLabel(row.source) }}</span>
            <template v-if="row.publishedAt">
              <span v-if="pubEdit && pubEdit.id === row.id" class="wg-pubedit">
                <input
                  type="date"
                  class="wg-input-sm"
                  v-model="pubEdit.val"
                  :max="isoToday"
                />
                <button class="wg-minibtn" @click="savePub(row)">Save</button>
                <button class="wg-minibtn ghost" @click="pubEdit = null">Cancel</button>
              </span>
              <button
                v-else-if="adminCanAdd"
                type="button"
                class="wg-publink"
                title="Publish date, detected from Search Console. Click to correct it."
                @click="pubEdit = { id: row.id, val: row.publishedAt }"
              ><b>Published {{ fmtDate(row.publishedAt) }}</b> <i class="material-icons">edit</i></button>
              <b v-else>Published {{ fmtDate(row.publishedAt) }}</b>
              <span
                class="dim"
                :title="`We started measuring this URL on ${fmtDate(row.createdAt)}. Performance and the 90-day settling period count from the publish date.`"
              >· tracking since {{ fmtDate(row.createdAt) }}</span>
            </template>
            <template v-else>Added {{ fmtDate(row.createdAt) }}</template>
          </div>
          <div v-if="row.lostSignal" class="wg-lostfix">
            <input
              class="wg-input-sm"
              placeholder="New address, e.g. example.com/new-slug"
              v-model="lostDrafts[row.id]"
              @keyup.enter="updateUrl(row)"
            />
            <button
              class="wg-btn-out"
              :disabled="fixingId === row.id || !(lostDrafts[row.id] || '').trim()"
              @click="updateUrl(row)"
            >{{ fixingId === row.id ? 'Updating…' : 'Update URL' }}</button>
          </div>
        </div>
        <span v-if="row.lostSignal" class="wg-badge-red" :title="lostHint">Lost signal</span>
        <span v-else class="wg-badge-green">Tracking</span>
        <button class="wg-xbtn" aria-label="Remove" @click="remove(row)">
          <i class="material-icons">close</i>
        </button>
      </div>
    </template>

    <!-- delivered articles needing a URL -->
    <div v-if="needsUrl.length > 0" class="wg-need">
      <div class="nh">
        {{ needsUrl.length }} delivered {{ needsUrl.length === 1 ? 'article' : 'articles' }} still
        {{ needsUrl.length === 1 ? 'needs a URL' : 'need URLs' }}:
      </div>
      <div v-for="row in needsUrl" :key="row.siloKeywordId" class="wg-need-row">
        <span class="name">{{ row.keyword }}</span>
        <input class="wg-input-sm" placeholder="example.com" v-model="needDrafts[row.siloKeywordId]" />
        <button class="wg-btn-out" :disabled="savingNeedId === row.siloKeywordId" @click="saveNeed(row)">
          {{ savingNeedId === row.siloKeywordId ? 'Saving…' : 'Save' }}
        </button>
      </div>
    </div>

    <!-- manual add: mission-control admin only -->
    <template v-if="adminCanAdd">
      <div class="wg-addurl-title">Add a URL manually</div>
      <div class="wg-addurl">
        <input class="wg-input-sm" placeholder="example.com" v-model="addUrl" @blur="runLookup" />
        <input class="wg-input-sm" placeholder="Keyword" v-model="addKeyword" />
        <button class="wg-btn-out" :disabled="adding || !addUrl.trim()" @click="add">
          <i class="material-icons">add</i> {{ adding ? 'Adding…' : 'Add URL' }}
        </button>
      </div>
      <div v-if="lookup && lookup.state === 'loading'" class="wg-lookup">
        <span class="wg-spin dark"></span> Checking Search Console…
      </div>
      <div v-else-if="lookup && lookup.state === 'found'" class="wg-lookup ok">
        <div>
          Found in Search Console. First seen by Google on <b>{{ fmtDate(lookup.firstSeen) }}</b>;
          we'll use that as the publish date.
        </div>
        <div class="lrow">
          <label>Published</label>
          <input type="date" class="wg-input-sm" v-model="addPublishedAt" :max="isoToday" />
          <span class="lh">Edit if the page went live earlier than Google found it.</span>
        </div>
      </div>
      <div v-else-if="lookup && lookup.state === 'notfound'" class="wg-lookup warn">
        Google hasn't seen this URL yet. If it just went live, that's normal, it will start in
        Early days. Otherwise double-check the address before tracking.
      </div>
      <div v-else-if="lookup && lookup.state === 'domain_mismatch'" class="wg-lookup err">
        This URL isn't on your connected site. We can only track pages covered by your Search
        Console property.
      </div>
      <div class="wg-addurl-note">
        We check every URL against Search Console to pick up its publish date automatically.
        Tracking starts immediately.
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'

const SOURCE_LABELS = {
  content_plan: { label: 'Content plan', cls: 'c-green' },
  content_only: { label: 'Content only', cls: 'c-blue' },
  ai_mapped: { label: 'AI mapped', cls: 'c-blue' },
  manual: { label: 'Manual', cls: 'c-grey' },
}

export default {
  name: 'PublishedUrlsCard',
  inject: ['wgToast'],
  props: {
    site: {
      type: Object,
      default: () => ({}),
    },
    /** Manual URL+keyword add is restricted to the mission-control WG team. */
    adminCanAdd: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      tracked: [],
      needsUrl: [],
      needDrafts: {},
      lostDrafts: {},
      fixingId: null,
      savingNeedId: null,
      addUrl: '',
      addKeyword: '',
      addPublishedAt: '',
      lookup: null,
      pubEdit: null,
      adding: false,
    }
  },
  watch: {
    'site.id': {
      handler () {
        this.load()
      },
      immediate: true,
    },
  },
  computed: {
    isoToday () {
      return moment().format('YYYY-MM-DD')
    },
    lostHint () {
      return 'The URL returns an error or moved - update the address to keep tracking.'
    },
    lostCount () {
      return this.tracked.filter((row) => row.lostSignal).length
    },
  },
  methods: {
    runLookup () {
      const url = this.addUrl.trim()
      if (!url || !this.site || !this.site.id) return
      this.lookup = { state: 'loading' }
      this.addPublishedAt = ''
      this.$store.dispatch('lookupWinsUrl', { siteId: this.site.id, url })
        .then((response) => {
          const data = response.data || {}
          if (data.status !== 'SUCCESS') {
            this.lookup = null
            return
          }
          this.lookup = { state: data.state, firstSeen: data.firstSeen }
          this.addPublishedAt = data.firstSeen || this.isoToday
        })
        .catch(() => {
          this.lookup = null
        })
    },
    savePub (row) {
      const val = this.pubEdit && this.pubEdit.val
      this.pubEdit = null
      if (!val || val === row.publishedAt) return
      this.$store.dispatch('updateWinsTrackedUrl', { trackedUrlId: row.id, body: { publishedAt: val } })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyPayload(response.data)
            this.wgToast('Publish date updated')
          } else {
            this.wgToast((response.data && response.data.msg) || 'Could not update the date', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not update the date', null, 'error')
        })
    },
    updateUrl (row) {
      const url = (this.lostDrafts[row.id] || '').trim()
      if (!url || this.fixingId) return
      this.fixingId = row.id
      this.$store.dispatch('updateWinsTrackedUrl', { trackedUrlId: row.id, body: { url } })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyPayload(response.data)
            this.wgToast('URL updated, tracking resumed. History is kept.')
          } else {
            this.wgToast((response.data && response.data.msg) || 'Could not update the URL', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not update the URL', null, 'error')
        })
        .finally(() => {
          this.fixingId = null
        })
    },
    tagLabel (source) {
      return (SOURCE_LABELS[source] || { label: source }).label
    },
    tagClass (source) {
      return (SOURCE_LABELS[source] || { cls: 'c-grey' }).cls
    },
    fmtDate (value) {
      return value ? moment(value).format('MMM D, YYYY') : '–'
    },
    applyPayload (data) {
      this.tracked = data.tracked || []
      this.needsUrl = data.needsUrl || []
      const drafts = {}
      this.needsUrl.forEach((row) => {
        drafts[row.siloKeywordId] = this.needDrafts[row.siloKeywordId] || ''
      })
      this.needDrafts = drafts
      const lostDrafts = {}
      this.tracked.forEach((row) => {
        if (row.lostSignal) lostDrafts[row.id] = this.lostDrafts[row.id] || ''
      })
      this.lostDrafts = lostDrafts
      this.$emit('counts-changed', { needsUrlCount: this.needsUrl.length })
    },
    load () {
      if (!this.site || !this.site.id) return
      this.$store.dispatch('listWinsTrackedUrls', this.site.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyPayload(response.data)
          }
        })
        .catch(() => {})
    },
    saveNeed (row) {
      const url = (this.needDrafts[row.siloKeywordId] || '').trim()
      if (!url || this.savingNeedId) return
      this.savingNeedId = row.siloKeywordId
      this.$store.dispatch('saveDeliveredUrl', { siloKeywordId: row.siloKeywordId, body: { url } })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyPayload(response.data)
            // tracking this keyword hides its AI mapping suggestions
            this.$emit('mapping-changed')
            this.wgToast('URL saved, now tracking. Publish date set from Search Console.')
          } else {
            this.wgToast('Could not save the URL', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not save the URL', null, 'error')
        })
        .finally(() => {
          this.savingNeedId = null
        })
    },
    add () {
      const url = this.addUrl.trim()
      if (!url || this.adding) return
      this.adding = true
      this.$store.dispatch('addWinsTrackedUrl', {
        siteId: this.site.id,
        body: {
          url,
          keyword: this.addKeyword.trim(),
          publishedAt: this.addPublishedAt || null,
        },
      })
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyPayload(response.data)
            this.addUrl = ''
            this.addKeyword = ''
            this.addPublishedAt = ''
            this.lookup = null
            this.wgToast('URL added to tracking')
          } else {
            const msg = (response.data && response.data.msg) || 'Could not add the URL'
            this.wgToast(msg, null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not add the URL', null, 'error')
        })
        .finally(() => {
          this.adding = false
        })
    },
    remove (row) {
      this.$store.dispatch('removeWinsTrackedUrl', row.id)
        .then((response) => {
          if (response.data && response.data.status === 'SUCCESS') {
            this.applyPayload(response.data)
            // un-tracking can bring the keyword's suggestions back
            this.$emit('mapping-changed')
            this.wgToast('URL removed from tracking')
          } else {
            this.wgToast('Could not remove the URL', null, 'error')
          }
        })
        .catch(() => {
          this.wgToast('Could not remove the URL', null, 'error')
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

/* publish date + GSC lookup (design_updated7) */
.wg-publink {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: none;
  padding: 0;
  font-size: inherit;
  color: $pop-teal;
  cursor: pointer;

  b {
    font-weight: 700;
  }

  .material-icons {
    font-size: 13px;
    color: $pop-ink-muted;
  }

  &:hover .material-icons {
    color: $pop-teal;
  }
}

.wg-pubedit {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  .wg-input-sm {
    padding: 4px 8px;
    width: auto;
  }
}

.wg-minibtn {
  border: 1px solid $pop-accent;
  background: $pop-accent;
  color: #fff;
  border-radius: 14px;
  padding: 3px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &.ghost {
    background: transparent;
    color: $pop-teal;
    border-color: $pop-border;
  }
}

.dim {
  color: $pop-ink-faint;
}

.wg-lookup {
  font-size: 0.82rem;
  line-height: 1.5;
  border-radius: 8px;
  padding: 12px 14px;
  margin-top: 12px;

  &.ok {
    background: $pop-success-bg;
    color: $pop-teal;
  }

  &.warn {
    background: $pop-warning-bg;
    color: #8a5a10;
  }

  &.err {
    background: $pop-danger-bg;
    color: $pop-danger;
  }

  .lrow {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;

    label {
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: $pop-ink-muted;
    }

    .wg-input-sm {
      padding: 5px 8px;
      width: auto;
    }

    .lh {
      font-size: 0.78rem;
      color: $pop-ink-muted;
    }
  }
}

.wg-addurl-note {
  font-size: 0.78rem;
  color: $pop-ink-muted;
  margin-top: 10px;
}

/* published URLs */
.wg-urlrow {
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid $wg-panel-line;
  border-radius: 8px;
  padding: 13px 16px;
  margin-bottom: 10px;

  .left {
    flex: 1;
    min-width: 0;
  }

  .u {
    font-size: 0.85rem;
    color: $pop-link;
    text-decoration: none;
    display: block;
    margin-bottom: 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 9px;
    font-size: 0.8rem;
    color: $pop-ink-muted;
    flex-wrap: wrap;
  }

  /* lost-signal fix row (design_updated8) */
  &.lost {
    border-color: #f0c8c8;
    background: #fffafa;
  }
}

.wg-lostfix {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-top: 10px;
  max-width: 440px;

  .wg-input-sm {
    flex: 1;
  }
}

.wg-xbtn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: $pop-ink-faint;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  transition: background 0.12s, color 0.12s;

  &:hover {
    background: $pop-danger-bg;
    color: $pop-danger;
  }

  .material-icons {
    font-size: 1.1rem;
  }
}

.wg-need {
  border: 1px solid $wg-panel-line;
  background: $pop-box-bg;
  border-radius: 12px;
  padding: 18px;
  margin: 18px 0;

  .nh {
    font-size: 0.9rem;
    font-weight: 700;
    color: $pop-teal;
    margin-bottom: 14px;
  }

  .wg-btn-out {
    border-color: $pop-border;
    color: $pop-teal;

    &:hover:not(:disabled) {
      background: #fff;
      border-color: $pop-accent;
      color: $pop-accent;
    }
  }
}

.wg-need-row {
  display: grid;
  grid-template-columns: 1fr 1.4fr auto;
  align-items: center;
  gap: 14px;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }

  .name {
    font-size: 0.85rem;
    color: $pop-teal;
  }
}

.wg-addurl-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: $pop-teal;
  margin: 4px 0 12px;
}

.wg-addurl {
  display: grid;
  grid-template-columns: 1.4fr 1fr auto;
  gap: 14px;
  align-items: center;
}
</style>
